import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import Seo from "./Seo";
import StructuredData from "./StructuredData";
import { useEnquiry } from "../context/EnquiryContext";

const PROCESS = [
  ["01", "Understand", "We start with your requirements, the way you live and how you want the space to work."],
  ["02", "Measure & plan", "Precise site measurements and a plan mapped to your room's dimensions and services."],
  ["03", "Design", "A tailored design with materials, finishes and internals finalised with you."],
  ["04", "Make", "Components are prepared through the relevant process, ready for a clean fit."],
  ["05", "Install", "Our team installs on site with careful attention to alignment and detailing."],
  ["06", "Quality & deliver", "Quality checks, then we hand over the completed, ready-to-use space."],
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export default function ServicePage({ cfg }) {
  const { openEnquiry } = useEnquiry();
  const consult = () => openEnquiry("consultation", cfg.slug);

  const ld = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: cfg.serviceName,
      serviceType: cfg.serviceName,
      description: cfg.seoDesc,
      areaServed: ["Noida", "Greater Noida", "Delhi NCR"],
      provider: { "@type": "HomeAndConstructionBusiness", name: "DIDIT", url: "https://didithomes.com/" },
      url: cfg.canonical,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://didithomes.com/" },
        { "@type": "ListItem", position: 2, name: cfg.breadcrumb, item: cfg.canonical },
      ],
    },
  ];

  return (
    <main className="bg-warm" data-testid={cfg.testid}>
      <Seo title={cfg.seoTitle} description={cfg.seoDesc} canonical={cfg.canonical} />
      <StructuredData id={cfg.slug} data={ld} />

      {/* HERO */}
      <section className="pt-36 md:pt-44 pb-16 md:pb-24">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          <Reveal>
            <Overline>{cfg.overline}</Overline>
            <h1 className="font-display text-4xl md:text-6xl font-light text-navy leading-[1.05] mt-5">{cfg.h1}</h1>
            <p className="text-ink/75 text-lg mt-6 max-w-md leading-relaxed">{cfg.heroSub}</p>
            <div className="flex flex-wrap gap-4 mt-9">
              <button onClick={consult} data-testid="sp-hero-consultation-btn"
                className="group flex items-center gap-2 rounded-full bg-navy text-warm px-7 py-4 text-sm font-medium hover:bg-terracotta transition-colors">
                Get Free Consultation <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <button onClick={() => scrollTo("sp-options")} data-testid="sp-hero-explore-btn"
                className="rounded-full border border-navy/25 text-navy px-7 py-4 text-sm font-medium hover:bg-navy hover:text-warm transition-colors">
                {cfg.secondaryCta}
              </button>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative overflow-hidden border border-navy/10 aspect-[4/3] md:aspect-[5/6]">
              <img src={cfg.heroImg} alt={cfg.heroAlt} className="w-full h-full object-cover" loading="eager" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-16 md:py-24 bg-white border-y border-navy/10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          <Reveal>
            <div className="relative overflow-hidden border border-navy/10 aspect-[4/3]">
              <img src={cfg.introImg} alt={cfg.introAlt} className="w-full h-full object-cover" loading="lazy" />
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <Overline>{cfg.introOverline}</Overline>
            <h2 className="font-display text-3xl md:text-5xl font-light text-navy leading-tight mt-5">{cfg.introH2}</h2>
            <p className="text-ink/75 leading-relaxed mt-6">{cfg.introP}</p>
            <div className="flex flex-wrap gap-2.5 mt-7">
              {cfg.chips.map((c) => (
                <span key={c} className="text-xs tracking-wide text-navy/70 border border-navy/15 rounded-full px-4 py-2">{c}</span>
              ))}
            </div>
            <p className="text-ink/70 text-sm mt-7 leading-relaxed">
              Every piece is part of DIDIT's end-to-end <Link to="/" className="text-terracotta hover:underline">home interiors</Link> offering —
              we <Link to="/how-it-works" className="text-terracotta hover:underline">design, install and deliver</Link>, so you work with one accountable team.
            </p>
          </Reveal>
        </div>
      </section>

      {/* OPTIONS (alternating rows) */}
      <section id="sp-options" className="py-16 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal>
            <Overline>{cfg.optionsOverline}</Overline>
            <h2 className="font-display text-3xl md:text-5xl font-light text-navy leading-tight mt-5 max-w-2xl">{cfg.optionsH2}</h2>
            {cfg.optionsSub && <p className="text-ink/70 mt-5 max-w-xl leading-relaxed">{cfg.optionsSub}</p>}
          </Reveal>
          <div className="mt-14 space-y-16 md:space-y-24">
            {cfg.options.map((o, i) => (
              <div key={o.name}
                className={`grid md:grid-cols-2 gap-8 md:gap-16 items-center ${i % 2 ? "md:[direction:rtl]" : ""}`}
                data-testid={`sp-option-${i}`}>
                <Reveal className="md:[direction:ltr]">
                  <div className="group relative overflow-hidden border border-navy/10 aspect-[4/3]">
                    <img src={o.img} alt={o.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  </div>
                </Reveal>
                <Reveal delay={0.05} className="md:[direction:ltr]">
                  <span className="font-display text-terracotta text-sm tracking-widest">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-display text-3xl md:text-4xl font-light text-navy mt-3 leading-tight">{o.name}</h3>
                  <p className="text-ink/75 leading-relaxed mt-5 max-w-md">{o.copy}</p>
                </Reveal>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-16 md:py-24 bg-navy text-warm">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal>
            <p className="text-[11px] tracking-[0.3em] uppercase text-terracotta">Design. Install. Deliver.</p>
            <h2 className="font-display text-3xl md:text-5xl font-light leading-tight mt-5 max-w-2xl">From first idea to a finished, ready-to-use space.</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-warm/10 mt-12 border border-warm/10">
            {PROCESS.map(([n, t, d], i) => (
              <Reveal key={n} delay={i * 0.05}>
                <div className="bg-navy p-8 h-full">
                  <span className="font-display text-terracotta text-sm tracking-widest">{n}</span>
                  <h3 className="font-display text-xl mt-3">{t}</h3>
                  <p className="text-warm/60 text-sm mt-3 leading-relaxed">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COMMITMENTS */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal>
            <Overline>Our commitments</Overline>
            <h2 className="font-display text-3xl md:text-5xl font-light text-navy leading-tight mt-5 max-w-2xl">Built to last, backed in writing.</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mt-12">
            {[
              ["10-year warranty", "On modulars, for peace of mind long after handover."],
              ["Quality checks", "Reviewed at key stages, through to final handover."],
              ["Transparent costing", "Clear, itemised pricing with no hidden surprises."],
              ["Timely delivery", "Planned schedules and dependable handovers."],
            ].map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.06}>
                <div className="h-full border border-navy/10 bg-white p-7">
                  <div className="w-10 h-10 rounded-full bg-terracotta/10 text-terracotta flex items-center justify-center"><Check size={18} /></div>
                  <h3 className="font-display text-xl text-navy mt-5">{t}</h3>
                  <p className="text-ink/65 text-sm mt-2 leading-relaxed">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + internal links */}
      <section className="py-16 md:py-24 bg-white border-t border-navy/10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal>
            <div className="bg-navy text-warm p-10 md:p-16">
              <h2 className="font-display text-3xl md:text-5xl font-light leading-tight max-w-2xl">{cfg.ctaH2}</h2>
              <p className="text-warm/70 mt-5 max-w-xl leading-relaxed">{cfg.ctaSub}</p>
              <button onClick={consult} data-testid="sp-cta-consultation-btn"
                className="group inline-flex items-center gap-2 rounded-full bg-terracotta text-white px-8 py-4 text-sm font-medium mt-9 hover:bg-warm hover:text-navy transition-colors">
                Get Free Consultation <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <div className="flex flex-wrap gap-x-8 gap-y-3 mt-10 pt-8 border-t border-warm/15 text-sm">
                <Link to="/" data-testid="sp-link-home" className="text-warm/70 hover:text-warm inline-flex items-center gap-1">Home <ArrowUpRight size={13} /></Link>
                <Link to="/modular-kitchens" data-testid="sp-link-kitchens" className="text-warm/70 hover:text-warm inline-flex items-center gap-1">Modular Kitchens <ArrowUpRight size={13} /></Link>
                <Link to="/how-it-works" data-testid="sp-link-how" className="text-warm/70 hover:text-warm inline-flex items-center gap-1">How It Works <ArrowUpRight size={13} /></Link>
                <button onClick={() => openEnquiry("quote", cfg.slug)} data-testid="sp-link-quote" className="text-warm/70 hover:text-warm inline-flex items-center gap-1">Get a Quote <ArrowUpRight size={13} /></button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
